import { ClickAwayListener, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { cloneElement, useMemo, useRef, useState } from 'react';

TapTooltip.propTypes = {
  children: PropTypes.element.isRequired,
  PopperProps: PropTypes.object,
};

/**
 * Tooltip de ícone de ajuda que também abre no toque.
 *
 * O `Tooltip` do MUI, no toque, só abre depois de 700 ms pressionado e fecha sozinho logo depois:
 * no celular um toque no ícone não mostra nada. Aqui o toque no ícone abre e fecha, e tocar em
 * qualquer outro lugar fecha. O ouvinte de toque do MUI fica desligado porque o temporizador de
 * saída dele fecharia o tooltip que o toque acabou de abrir. No desktop, hover e foco continuam
 * iguais, e o clique do mouse só abre: o hover já abriu, e alternar fecharia o texto que a pessoa
 * passou o mouse para ler.
 *
 * O clique de fora só é ouvido com o tooltip aberto: este componente está em todo ícone de ajuda
 * dos formulários, e cada ouvinte no `document` roda a cada clique na página. O popper fica num
 * portal, fora do ícone, então é o ref dele que separa o toque no texto do toque de fora.
 */
export default function TapTooltip({ children, PopperProps, ...tooltipProps }) {
  const [open, setOpen] = useState(false);
  const popperRef = useRef(null);
  const pointerTypeRef = useRef(null);
  const popperProps = useMemo(() => ({ ...PopperProps, ref: popperRef }), [PopperProps]);

  const handleClickAway = (event) => {
    if (popperRef.current?.contains(event.target)) return;
    setOpen(false);
  };

  return (
    <ClickAwayListener
      onClickAway={handleClickAway}
      mouseEvent={open ? 'onClick' : false}
      touchEvent={open ? 'onTouchEnd' : false}
    >
      <Tooltip
        {...tooltipProps}
        PopperProps={popperProps}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        disableTouchListener
      >
        {cloneElement(children, {
          onPointerDown: (event) => {
            children.props.onPointerDown?.(event);
            pointerTypeRef.current = event.pointerType;
          },
          onClick: (event) => {
            children.props.onClick?.(event);
            // O `click` não diz de onde veio: o `pointerdown` que o precede diz. Sem ele (teclado),
            // o clique só abre, como o do mouse.
            const isTouch = pointerTypeRef.current === 'touch';
            pointerTypeRef.current = null;
            setOpen((value) => (isTouch ? !value : true));
          },
        })}
      </Tooltip>
    </ClickAwayListener>
  );
}
